import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { Stack } from "@mui/material";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useHistory } from "react-router-dom";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon,
} from "@material-ui/icons";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import WhatsAppModalCompany from "../../components/CompanyWhatsapps";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    borderRadius: 10,
    boxShadow: "rgba(0,0,0,0.1) 0px 4px 12px",
    ...theme.scrollbarStyles,
  },
  /* table */
  tableHead: {
    backgroundColor: theme.palette.barraSuperior,
    "& th": { color: "#fff", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" },
  },
  totalRow: {
    backgroundColor: theme.palette.barraSuperior,
    "& td": { color: "#fff", fontWeight: 700 },
  },
  /* mobile card list */
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "8px 4px 16px",
  },
  card: {
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.09)",
    borderRadius: 12,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  companyName: {
    fontWeight: 700,
    fontSize: 15,
    color: "#1e293b",
  },
  cardStats: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  statPill: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
  },
  totalCard: {
    background: theme.palette.barraSuperior,
    borderRadius: 12,
    padding: "12px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#fff",
    marginTop: 4,
  },
  totalLabel: { fontWeight: 700, fontSize: 14 },
  totalStats: { display: "flex", gap: 16, alignItems: "center" },
  totalStat: { display: "flex", flexDirection: "column", alignItems: "center" },
  totalStatNum: { fontWeight: 800, fontSize: 18 },
  totalStatLabel: { fontSize: 10, opacity: 0.85, textTransform: "uppercase", letterSpacing: "0.05em" },
  /* tooltip */
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0,0,0,0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450,
  },
  tooltipPopper: { textAlign: "center" },
  buttonProgress: { color: green[500] },
}));

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();
  return (
    <Tooltip
      arrow
      classes={{ tooltip: classes.tooltip, popper: classes.tooltipPopper }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">{title}</Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const AllConnections = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);
  const { list } = useCompanies();
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [loadingComp, setLoadingComp] = useState(false);
  const [whats, setWhats] = useState([]);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterConnections, setFilterConnections] = useState([]);
  const [companyWhatsApps, setCompanyWhatsApps] = useState(null);
  const confirmationModalInitialState = { action: "", title: "", message: "", whatsAppId: "", open: false };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);

  const history = useHistory();
  if (!user.super) history.push("/tickets");

  useEffect(() => {
    setLoadingWhatsapp(true);
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/all/?session=0");
        setWhats(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingWhatsapp(false);
      }
    };
    fetchSession();
  }, []);

  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    setLoadingComp(true);
    try {
      const companyList = await list();
      setCompanies(companyList);
    } catch {
      toast.error(i18n.t("connections.toasts.loadError") || "No se pudo cargar la lista de empresas");
    }
    setLoadingComp(false);
  };

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;
      api.post("/facebook", { facebookUserId: id, facebookUserToken: accessToken })
        .then(() => toast.success(i18n.t("connections.facebook.success")))
        .catch((error) => toastError(error));
    }
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;
      api.post("/facebook", { addInstagram: true, facebookUserId: id, facebookUserToken: accessToken })
        .then(() => toast.success(i18n.t("connections.facebook.success")))
        .catch((error) => toastError(error));
    }
  };

  const handleOpenWhatsAppModal = (whatsappsFilter, comp) => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    if (whatsappsFilter?.length > 0) {
      setFilterConnections(whatsappsFilter);
      setCompanyWhatsApps(comp);
    }
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
    setFilterConnections([]);
    setCompanyWhatsApps(null);
  }, []);

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    setConfirmModalInfo({
      action,
      title: i18n.t(`connections.confirmationModal.${action}Title`),
      message: i18n.t(`connections.confirmationModal.${action}Message`),
      whatsAppId,
    });
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try { await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`); }
      catch (err) { toastError(err); }
    }
    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) { toastError(err); }
    }
    setConfirmModalInfo(confirmationModalInitialState);
  };

  const connectedCount = (companyId) =>
    whats.filter((w) => w?.companyId === companyId && w?.status === "CONNECTED").length;
  const disconnectedCount = (companyId) =>
    whats.filter((w) => w?.companyId === companyId && w?.status !== "CONNECTED").length;
  const totalCount = (companyId) =>
    whats.filter((w) => w?.companyId === companyId).length;

  const totalConnected = whats.filter((w) => w?.status === "CONNECTED").length;
  const totalDisconnected = whats.filter((w) => w?.status !== "CONNECTED").length;
  const totalAll = whats.length;

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={() => { setSelectedWhatsApp(null); setQrModalOpen(false); }}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModalCompany
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        filteredWhatsapps={filterConnections}
        companyInfos={companyWhatsApps}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />

      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <Paper className={classes.mainPaper} style={{ overflow: "hidden" }} variant="outlined">
          <MainHeader>
            <Stack>
              <Typography
                variant="h5"
                style={{ fontWeight: "bold", marginLeft: 10, marginTop: 10 }}
                gutterBottom
              >
                {i18n.t("connections.title")}
              </Typography>
              <Typography style={{ marginTop: -10, marginLeft: 10 }} variant="caption" color="textSecondary">
                {i18n.t("connections.connectYourServiceChannelsToReceiveMessagesAndStartConversationsWithYourCustomers")}
              </Typography>
            </Stack>

            <MainHeaderButtonsWrapper>
              <PopupState variant="popover" popupId="add-connection-menu">
                {(popupState) => (
                  <React.Fragment>
                    <Menu {...bindMenu(popupState)}>
                      <MenuItem onClick={() => { handleOpenWhatsAppModal(); popupState.close(); }}>
                        <WhatsApp fontSize="small" style={{ marginRight: 10 }} />
                        WhatsApp
                      </MenuItem>
                      <FacebookLogin
                        appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                        autoLoad={false}
                        fields="name,email,picture"
                        version="20.0"
                        scope="public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,pages_manage_engagement,pages_read_user_content,business_management"
                        callback={responseFacebook}
                        render={(renderProps) => (
                          <MenuItem onClick={renderProps.onClick}>
                            <Facebook fontSize="small" style={{ marginRight: 10 }} />
                            Facebook
                          </MenuItem>
                        )}
                      />
                      <FacebookLogin
                        appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                        autoLoad={false}
                        fields="name,email,picture"
                        version="20.0"
                        scope="public_profile,instagram_basic,instagram_manage_messages,instagram_manage_comments,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,pages_manage_engagement,pages_read_user_content,business_management"
                        callback={responseInstagram}
                        render={(renderProps) => (
                          <MenuItem onClick={renderProps.onClick}>
                            <Instagram fontSize="small" style={{ marginRight: 10 }} />
                            Instagram
                          </MenuItem>
                        )}
                      />
                    </Menu>
                  </React.Fragment>
                )}
              </PopupState>
            </MainHeaderButtonsWrapper>
          </MainHeader>

          <Stack
            style={{
              overflowY: "auto",
              padding: isMobile ? "12px 8px" : "20px",
              backgroundColor: "rgba(244,244,244,0.53)",
              borderRadius: 5,
              height: "93%",
            }}
          >
            {isMobile ? (
              /* ── MOBILE: tarjetas ── */
              <div className={classes.cardList}>
                {loadingWhatsapp ? (
                  <Typography color="textSecondary" align="center" style={{ padding: 24 }}>
                    Cargando...
                  </Typography>
                ) : (
                  <>
                    {companies.map((company) => {
                      const connected = connectedCount(company.id);
                      const disconnected = disconnectedCount(company.id);
                      const total = totalCount(company.id);
                      return (
                        <div key={company.id} className={classes.card}>
                          <div className={classes.cardHeader}>
                            <span className={classes.companyName}>{company.name}</span>
                            {user.profile === "admin" && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenWhatsAppModal(
                                    whats.filter((w) => w?.companyId === company.id),
                                    company
                                  )
                                }
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                          </div>
                          <div className={classes.cardStats}>
                            <span
                              className={classes.statPill}
                              style={{ background: "rgba(34,197,94,0.12)", color: "#16a34a" }}
                            >
                              <WifiIcon style={{ fontSize: 14 }} />
                              {connected} {i18n.t("connections.connectedConnections")}
                            </span>
                            <span
                              className={classes.statPill}
                              style={{ background: "rgba(239,68,68,0.12)", color: "#dc2626" }}
                            >
                              <WifiOffIcon style={{ fontSize: 14 }} />
                              {disconnected} {i18n.t("connections.disconnectedConnections")}
                            </span>
                            <span
                              className={classes.statPill}
                              style={{ background: "rgba(100,116,139,0.1)", color: "#475569" }}
                            >
                              {total} {i18n.t("connections.totalConnections")}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Total card */}
                    <div className={classes.totalCard}>
                      <span className={classes.totalLabel}>{i18n.t("connections.total")}</span>
                      <div className={classes.totalStats}>
                        <div className={classes.totalStat}>
                          <span className={classes.totalStatNum}>{totalConnected}</span>
                          <span className={classes.totalStatLabel}>Activas</span>
                        </div>
                        <div className={classes.totalStat}>
                          <span className={classes.totalStatNum}>{totalDisconnected}</span>
                          <span className={classes.totalStatLabel}>Inactivas</span>
                        </div>
                        <div className={classes.totalStat}>
                          <span className={classes.totalStatNum}>{totalAll}</span>
                          <span className={classes.totalStatLabel}>Total</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* ── DESKTOP: tabla ── */
              <Paper>
                <Table size="small">
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell align="center">{i18n.t("connections.client")}</TableCell>
                      <TableCell align="center">{i18n.t("connections.connectedConnections")}</TableCell>
                      <TableCell align="center">{i18n.t("connections.disconnectedConnections")}</TableCell>
                      <TableCell align="center">{i18n.t("connections.totalConnections")}</TableCell>
                      {user.profile === "admin" && (
                        <TableCell align="center">{i18n.t("connections.table.actions")}</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingWhatsapp ? (
                      <TableRowSkeleton />
                    ) : (
                      <>
                        {companies.map((company) => {
                          const connected = connectedCount(company.id);
                          const disconnected = disconnectedCount(company.id);
                          const total = totalCount(company.id);
                          return (
                            <TableRow key={company.id} hover>
                              <TableCell align="center">{company.name}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={connected}
                                  size="small"
                                  style={{
                                    background: "rgba(34,197,94,0.12)",
                                    color: "#16a34a",
                                    fontWeight: 700,
                                    minWidth: 32,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={disconnected}
                                  size="small"
                                  style={{
                                    background: disconnected > 0 ? "rgba(239,68,68,0.12)" : "rgba(100,116,139,0.1)",
                                    color: disconnected > 0 ? "#dc2626" : "#64748b",
                                    fontWeight: 700,
                                    minWidth: 32,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">{total}</TableCell>
                              {user.profile === "admin" && (
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleOpenWhatsAppModal(
                                        whats.filter((w) => w?.companyId === company.id),
                                        company
                                      )
                                    }
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                        <TableRow className={classes.totalRow}>
                          <TableCell align="center">{i18n.t("connections.total")}</TableCell>
                          <TableCell align="center">{totalConnected}</TableCell>
                          <TableCell align="center">{totalDisconnected}</TableCell>
                          <TableCell align="center">{totalAll}</TableCell>
                          {user.profile === "admin" && <TableCell />}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Stack>
        </Paper>
      )}
    </MainContainer>
  );
};

export default AllConnections;
